import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { patientsApi } from "../services/api";
import { toast } from "react-toastify";

const PatientRegistration = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    fullName: "",
    phone: "",
    email: "",
    dateOfBirth: "",
    gender: "",
    address: "",
    isChronic: false,
    chronicCondition: "",
    smsConsent: false,
    emailConsent: false,
    telegramConsent: false,
    telegramUsername: ""
  });

  const handleCheckbox = (field) => {
    setFormData(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const validate = () => {
    if (!formData.fullName.trim()) {
      toast.error("Full name is required");
      return false;
    }
    if (!formData.phone.trim()) {
      toast.error("Phone number is required");
      return false;
    }
    // Ethiopian phone validation: 09..., +2519..., 9...
    const phoneRegex = /^(?:\+?251)?[79]\d{8}$/;
    if (!phoneRegex.test(formData.phone.replace(/\D/g, ""))) {
      toast.error("Invalid Ethiopian phone number (e.g., 0911223344)");
      return false;
    }
    if (formData.emailConsent && !formData.email) {
      toast.error("Email is required for email consent");
      return false;
    }
    if (formData.telegramConsent && !formData.telegramUsername) {
      toast.error("Telegram username (e.g., @john) is required for Telegram consent");
      return false;
    }
    if (formData.telegramConsent && !/^@[a-zA-Z0-9_]{5,32}$/.test(formData.telegramUsername)) {
      toast.error("Invalid Telegram username (e.g., @john_doe)");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const response = await patientsApi.register({
        ...formData,
        phone: formData.phone.replace(/\D/g, ""), // store digits only
        telegramUsername: formData.telegramUsername.startsWith("@") 
          ? formData.telegramUsername 
          : `@${formData.telegramUsername}`
      });

      toast.success(`✅ Patient ${response.data.fullName} registered successfully!`);
      navigate("/patients");
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to register patient";
      toast.error(`❌ ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container py-4">
      <div className="row justify-content-center">
        <div className="col-lg-8">
          <div className="card shadow-sm">
            <div className="card-header bg-primary text-white">
              <h4 className="mb-0">
                <i className="bi bi-person-plus me-2"></i>
                Register New Patient
              </h4>
            </div>
            <div className="card-body">
              <form onSubmit={handleSubmit}>
                {/* Personal Info */}
                <div className="row mb-3">
                  <div className="col-md-6">
                    <label className="form-label">Full Name <span className="text-danger">*</span></label>
                    <input
                      type="text"
                      className="form-control"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Phone <span className="text-danger">*</span></label>
                    <input
                      type="tel"
                      className="form-control"
                      name="phone"
                      placeholder="e.g., 0911223344"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                    />
                    <small className="text-muted">Ethiopian number (09..., 07...)</small>
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-md-6">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      className="form-control"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label">Date of Birth</label>
                    <input
                      type="date"
                      className="form-control"
                      name="dateOfBirth"
                      value={formData.dateOfBirth}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="row mb-3">
                  <div className="col-md-4">
                    <label className="form-label">Gender</label>
                    <select
                      className="form-select"
                      name="gender"
                      value={formData.gender}
                      onChange={handleChange}
                    >
                      <option value="">Select</option>
                      <option value="M">Male</option>
                      <option value="F">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div className="col-md-8">
                    <label className="form-label">Address</label>
                    <textarea
                      className="form-control"
                      name="address"
                      rows="2"
                      value={formData.address}
                      onChange={handleChange}
                    ></textarea>
                  </div>
                </div>

                {/* Chronic Condition */}
                <div className="card mb-4 border-primary">
                  <div className="card-header bg-light">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        id="isChronic"
                        checked={formData.isChronic}
                        onChange={() => handleCheckbox("isChronic")}
                      />
                      <label className="form-check-label" htmlFor="isChronic">
                        <strong>Chronic Patient</strong> (e.g., HTN, DM, Asthma)
                      </label>
                    </div>
                  </div>
                  <div className="card-body">
                    {formData.isChronic && (
                      <div className="mb-3">
                        <label className="form-label">Chronic Condition</label>
                        <input
                          type="text"
                          className="form-control"
                          name="chronicCondition"
                          placeholder="e.g., Hypertension"
                          value={formData.chronicCondition}
                          onChange={handleChange}
                        />
                      </div>
                    )}
                  </div>
                </div>

                {/* Consent Preferences */}
                <div className="card mb-4 border-info">
                  <div className="card-header bg-light">
                    <h5 className="mb-0">Notification Preferences</h5>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-4">
                        <div className="form-check mb-2">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="smsConsent"
                            checked={formData.smsConsent}
                            onChange={() => handleCheckbox("smsConsent")}
                          />
                          <label className="form-check-label" htmlFor="smsConsent">
                            <i className="bi bi-phone me-1 text-success"></i> SMS
                          </label>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="form-check mb-2">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="emailConsent"
                            checked={formData.emailConsent}
                            onChange={() => handleCheckbox("emailConsent")}
                          />
                          <label className="form-check-label" htmlFor="emailConsent">
                            <i className="bi bi-envelope me-1 text-primary"></i> Email
                          </label>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="form-check mb-2">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="telegramConsent"
                            checked={formData.telegramConsent}
                            onChange={() => handleCheckbox("telegramConsent")}
                          />
                          <label className="form-check-label" htmlFor="telegramConsent">
                            <i className="bi bi-telegram me-1 text-info"></i> Telegram
                          </label>
                        </div>
                      </div>
                    </div>

                    {formData.telegramConsent && (
                      <div className="mt-3">
                        <label className="form-label">Telegram Username</label>
                        <div className="input-group">
                          <span className="input-group-text">@</span>
                          <input
                            type="text"
                            className="form-control"
                            name="telegramUsername"
                            placeholder="e.g., john_doe"
                            value={formData.telegramUsername.replace("@", "")}
                            onChange={(e) => setFormData(prev => ({
                              ...prev,
                              telegramUsername: e.target.value
                            }))}
                          />
                        </div>
                        <small className="text-muted">Must be your Telegram @username</small>
                      </div>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="d-flex gap-2">
                  <button
                    type="submit"
                    className="btn btn-success px-4"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Registering...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check-circle me-2"></i>
                        Register Patient
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => navigate(-1)}
                  >
                    <i className="bi bi-arrow-left me-2"></i>
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientRegistration;